import { type CSSProperties, useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { BOOKING_GUIDE_EVENT, requestBookingGuide } from '../lib/booking'

const LAUNCHER_GEOMETRY_EVENT = 'twilabeth:launcher-geometry'
const REQUEST_LAUNCHER_GEOMETRY_EVENT = 'twilabeth:request-launcher-geometry'

interface LauncherGeometry {
  left: number
  top: number
  width: number
  height: number
  borderRadius: string
}

interface BookMeButtonProps {
  className?: string
  label?: string
}

export function BookMeButton({ className = '', label = 'Book Me' }: BookMeButtonProps) {
  return (
    <button
      type="button"
      className={`book-me ${className}`.trim()}
      onClick={requestBookingGuide}
    >
      <span className="book-label">{label}</span>
      <span className="book-arrow" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M5.5 10h8.25M10.75 6.75 14 10l-3.25 3.25" />
        </svg>
      </span>
    </button>
  )
}

function fallbackLauncherGeometry(): LauncherGeometry {
  const mobile = window.matchMedia('(max-width: 520px)').matches
  const size = 60
  const offset = mobile ? 14 : 22
  return {
    left: window.innerWidth - offset - size,
    top: window.innerHeight - offset - size,
    width: size,
    height: size,
    borderRadius: '50%',
  }
}

function isLauncherGeometry(value: unknown): value is LauncherGeometry {
  if (!value || typeof value !== 'object') return false
  const geometry = value as Partial<LauncherGeometry>
  return [geometry.left, geometry.top, geometry.width, geometry.height].every(Number.isFinite)
    && Number(geometry.width) > 0
    && Number(geometry.height) > 0
    && typeof geometry.borderRadius === 'string'
}

export function BookingGuide() {
  const [visible, setVisible] = useState(false)
  const [target, setTarget] = useState<LauncherGeometry>({ left: 0, top: 0, width: 0, height: 0, borderRadius: '50%' })

  useEffect(() => {
    let timer = 0
    const requestGeometry = () => {
      window.dispatchEvent(new Event(REQUEST_LAUNCHER_GEOMETRY_EVENT))
    }
    const syncFallbackAndRequest = () => {
      setTarget(fallbackLauncherGeometry())
      requestGeometry()
    }
    const receiveGeometry = (event: Event) => {
      if (!(event instanceof CustomEvent) || !isLauncherGeometry(event.detail)) return
      setTarget(event.detail)
    }
    const show = () => {
      window.clearTimeout(timer)
      syncFallbackAndRequest()
      setVisible(true)
      timer = window.setTimeout(() => setVisible(false), 3600)
    }
    window.addEventListener(BOOKING_GUIDE_EVENT, show)
    window.addEventListener(LAUNCHER_GEOMETRY_EVENT, receiveGeometry)
    window.addEventListener('resize', syncFallbackAndRequest)
    window.addEventListener('orientationchange', syncFallbackAndRequest)
    syncFallbackAndRequest()
    return () => {
      window.removeEventListener(BOOKING_GUIDE_EVENT, show)
      window.removeEventListener(LAUNCHER_GEOMETRY_EVENT, receiveGeometry)
      window.removeEventListener('resize', syncFallbackAndRequest)
      window.removeEventListener('orientationchange', syncFallbackAndRequest)
      window.clearTimeout(timer)
    }
  }, [])

  const guideStyle = {
    '--widget-left': `${target.left}px`,
    '--widget-top': `${target.top}px`,
    '--widget-width': `${target.width}px`,
    '--widget-height': `${target.height}px`,
    '--widget-radius': target.borderRadius,
  } as CSSProperties

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="booking-guide"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={guideStyle}
          aria-live="polite"
          role="status"
        >
          <motion.div className="guide-orbit" initial={{ scale: 0.5 }} animate={{ scale: [0.5, 1.14, 1] }} transition={{ duration: 1.2 }} />
          <motion.div className="guide-line" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.9, ease: 'easeOut' }} />
          <motion.p initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35 }}>Use “Chat with us” to book</motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
